/*!
 * transportState.js — the transport's state, and who may change it
 * © 2026 Rob Canning — GPLv3
 *
 * Where the playhead is, what time that is, whether the score plays, how
 * fast, and the score's extent: one module holds them. The transport
 * (transport/, RAF.js, socket.js, and the score cues that start, stop and
 * pace it) changes them through the named setters below; everything else
 * reads them, from `transport`:
 *
 *     import { transport } from "../system/transportState.js";
 *     if (transport.isPlaying) ... transport.playheadX ...
 *
 * An engine leaf: it imports nothing, so Oscilla Live (no transport) loads it
 * as the score does, and reads undefined where the score has no value yet.
 *
 * The old window names stay, as accessors onto the same state: project
 * extensions and the browser test rigs read them, and some write them
 * (polygonfield moves window.playheadX to follow its polygons; the docs
 * screenshot script and demo-connections' assert.js place the playhead so).
 * A write there lands here. Units as before: elapsedTime is milliseconds,
 * playheadX and scoreWidth world units, duration ms.
 */

const state = {
  playheadX: undefined,          // world units along the scroll axis
  elapsedTime: undefined,        // ms of score time at playheadX
  isPlaying: undefined,
  isMusicalPause: undefined,     // held by pause()/stop(): the piece's time runs on
  speedMultiplier: undefined,    // 1 = the score's own pace; negative plays backwards
  scoreWidth: undefined,         // the score's length along its scroll axis, world units
  duration: undefined,           // ms the playhead takes to cross scoreWidth
  scrollReversed: undefined,     // right to left / bottom up: starts at scoreWidth
  ignoreNextSync: undefined,     // skip the next sync (socket.js), set by a move or a play
  recentlyRecalculatedPlayhead: undefined,   // the sync hold after a move (holdSync)
};

for (const name of Object.keys(state)) {
  // a value a page set before this module loaded is kept
  const had = Object.getOwnPropertyDescriptor(window, name);
  if (had && "value" in had) state[name] = had.value;
  Object.defineProperty(window, name, {
    get: () => state[name],
    set: (v) => { state[name] = v; },
    configurable: true,
    enumerable: true,
  });
}

/** The transport, read-only. */
export const transport = Object.freeze({
  get playheadX() { return state.playheadX; },
  get elapsedTime() { return state.elapsedTime; },
  get isPlaying() { return state.isPlaying; },
  get isMusicalPause() { return state.isMusicalPause; },
  get speedMultiplier() { return state.speedMultiplier; },
  get scoreWidth() { return state.scoreWidth; },
  get duration() { return state.duration; },
  get scrollReversed() { return state.scrollReversed; },
  get ignoreNextSync() { return state.ignoreNextSync; },
  get recentlyRecalculatedPlayhead() { return state.recentlyRecalculatedPlayhead; },
});

// ── the setters: the transport's only ─────────────────────────────────────

/**
 * The playhead's world x, and the time there when it is given. Only this:
 * a move with what goes with it (cues re-armed, the room told) is
 * relocatePlayhead's (oscillaTransport.js); the frame loop, the drift
 * correction and a drag's steps move it with this alone.
 */
export function movePlayhead(x, elapsedMs) {
  state.playheadX = x;
  if (elapsedMs !== undefined) state.elapsedTime = elapsedMs;
}

export function setElapsed(ms) { state.elapsedTime = ms; }

/**
 * Playing or not. musicalPause, when given, says whether a stop is the
 * piece's own (pause(), stop(): its time runs on) or the performer's.
 */
export function setPlaying(on, { musicalPause } = {}) {
  state.isPlaying = on;
  if (musicalPause !== undefined) state.isMusicalPause = musicalPause;
}

export function setMusicalPause(on) { state.isMusicalPause = on; }

export function setSpeedMultiplier(m) { state.speedMultiplier = m; }

/** The score's extent, any of the three: what is not given stays. */
export function setScoreGeometry({ scoreWidth, duration, reversed } = {}) {
  if (scoreWidth !== undefined) state.scoreWidth = scoreWidth;
  if (duration !== undefined) state.duration = duration;
  if (reversed !== undefined) state.scrollReversed = reversed;
}

// Until the server has a jump, its clock still says the old place: syncs and
// drift correction hold off for half a second (recentlyRecalculatedPlayhead),
// and the first sync after that is skipped too and clears ignoreNextSync
// (socket.js). One timer, so a second jump inside the half second holds the
// full half second from itself.
export const SYNC_HOLD_MS = 500;
let syncHoldTimer = null;

export function holdSync() {
  state.ignoreNextSync = true;
  state.recentlyRecalculatedPlayhead = true;
  clearTimeout(syncHoldTimer);
  syncHoldTimer = setTimeout(() => { state.recentlyRecalculatedPlayhead = false; }, SYNC_HOLD_MS);
}

/** Take the room's position as it comes (the first sync on connecting). */
export function releaseSync() {
  clearTimeout(syncHoldTimer);
  state.ignoreNextSync = false;
  state.recentlyRecalculatedPlayhead = false;
}

/** Skip (true) or take (false) the next sync, the hold left as it is. */
export function skipNextSync(on) { state.ignoreNextSync = on; }
